<template>
  <v-container fluid class="customers-page">
    <!-- Search -->
    <v-row class="align-center">
      <v-col cols="12">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search customers..."
          variant="outlined"
          hide-details
          density="comfortable"
          class="search-field"
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- Customer List -->
    <div v-if="isLoading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <v-row v-else-if="filteredCustomers.length > 0">
      <v-col
        v-for="customer in filteredCustomers"
        :key="customer.id"
        cols="12"
        sm="6"
        lg="4"
        class="pb-0"
      >
        <AppCard
          class="customer-card clickable-card h-100"
          @click="goToDetail(customer.id)"
        >
          <div class="customer-card-content d-flex align-center">
            <v-avatar
              size="56"
              :color="customer.color"
              class="elevation-1 mr-4"
            >
              <span class="text-h5 text-white">{{ getInitials(customer.name) }}</span>
            </v-avatar>
            
            <div class="customer-info min-width-0 flex-grow-1">
              <h4 class="font-weight-bold text-truncate mb-0">{{ customer.name }}</h4>
              <div class="text-body-2 text-medium-emphasis text-truncate my-2">
                <v-icon size="14" class="mr-1">mdi-map-marker-outline</v-icon>
                {{ customer.address }}
              </div>
              <div class="d-flex align-center flex-wrap gap-1 mt-1">
                <v-chip v-if="customer.appointments > 0" size="small" color="" variant="tonal" label class="mr-2 mb-2">
                  {{ customer.appointments }} Appt.
                </v-chip>
                <v-chip v-if="customer.subscriptions > 0" size="small" color="" variant="tonal" label class="mr-2 mb-2">
                  {{ customer.subscriptions }} Subsc.
                </v-chip>
                <v-chip v-if="customer.inquiries > 0" size="small" color="" variant="tonal" label class="mr-2 mb-2">
                  {{ customer.inquiries }} Inq.
                </v-chip>
              </div>
            </div>

            <v-icon color="grey-lighten-1">mdi-chevron-right</v-icon>
          </div>
        </AppCard>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-row v-else class="mt-12">
      <v-col cols="12" class="text-center">
        <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-account-search-outline</v-icon>
        <h3 class="text-h5 text-medium-emphasis">{{ errorMessage ? "Unable to load customers" : "No customers found" }}</h3>
        <p class="text-body-1 text-disabled">{{ errorMessage || "Try adjusting your search" }}</p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import AppCard from '@/shared/components/ui/AppCard.vue';
import { fetchCustomers } from "@/modules/vendor/services/vendors.api";

const router = useRouter();

interface Customer {
  id: number;
  name: string;
  address: string;
  appointments: number;
  subscriptions: number;
  inquiries: number;
  color: string;
}

const searchQuery = ref('');
const isLoading = ref(false);
const errorMessage = ref("");

const customers = ref<Customer[]>([]);


const filteredCustomers = computed(() => {
  return customers.value.filter(c => {
    return c.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
           c.address.toLowerCase().includes(searchQuery.value.toLowerCase());
  });
});

function asArray(response: any): any[] {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.customers)) return data.customers;
  return [];
}

function formatAddress(item: any) {
  if (typeof item?.address === "string") return item.address;
  const address = item?.address || {};
  return [address.line_1, address.line_2, address.city, address.state, address.zip]
    .filter(Boolean)
    .join(", ");
}

function normalizeCustomer(item: any, index: number): Customer {
  const name = item?.name || item?.full_name || [item?.first_name, item?.last_name].filter(Boolean).join(" ");
  return {
    id: Number(item?.id ?? index),
    name: String(name || "Customer"),
    address: formatAddress(item),
    appointments: Number(item?.appointments ?? item?.appointment_count ?? 0),
    subscriptions: Number(item?.subscriptions ?? item?.subscription_count ?? 0),
    inquiries: Number(item?.inquiries ?? item?.inquiry_count ?? 0),
    color: "blue-darken-2",
  };
}

async function loadCustomers() {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const response = await fetchCustomers();
    customers.value = asArray(response).map(normalizeCustomer);
  } catch (error: any) {
    errorMessage.value = error?.response?.data?.message || error?.message || "Failed to load customers.";
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  loadCustomers();
});

const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

const goToDetail = (id: number) => {
  router.push({ name: 'vendor.customers.detail', params: { id } }).catch(() => {});
};
</script>

<style scoped>
.customers-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.customer-card {
  /* border: 1px solid rgba(0, 0, 0, 0.05); */
  padding: 0;
}

.clickable-card {
  cursor: pointer;
}

.customer-card-content {
  padding: 8px;
}

.search-field :deep(.v-field__outline) {
  --v-field-border-opacity: 0.1;
}

@media (max-width: 600px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>




